import React from 'react';
import { Loader, ReloadIconButton, EmployeeSelectModal } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import { notifications, extractApiErrorMessage, getFilters, saveFilters } from '../../utils';
import { Button, Icon, IconButton, Tooltip } from '@mui/material';
import { V4_ICON_BUTTON } from '../../theme/v4Compat';
import { TaskListStatus } from '../../models/common';
import { FilterListOff } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfWeek, endOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import useFeatures, { FeatureType } from '../../hooks/features';
import useAuthContext from '../../hooks/useAuthContext';
import { useStartOfWorkingDays } from '../../hooks/useStartOfWorkingDays';
import TaskPlanTableRow from './components/TaskPlanTableRow';
import ScheduleWeekSelector from './components/ScheduleWeekSelector';
import {
  HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel,
  HealthSystemDomainUsersResultUserViewModel
} from '../../api/HealthSystemApi';

interface TaskPlansFilters {
  dateFrom: string | null;
  dateTo: string | null;
  employeeIds: string[];
}

const WEEK_STARTS_ON = 1 as const; // Monday

const TaskPlans: React.FC<any> = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const apiService = new ApiService();
  const { hasFeature } = useFeatures();

  const showUpdateAction = hasFeature(FeatureType.USE_TASK_OCCURRENCE_UPDATE);
  const storageKey = `TASK-PLANS-FARM-${state.farmId}`;

  const saved = getFilters<TaskPlansFilters>(storageKey);

  const [taskPlans, setTaskPlans] = React.useState<HealthSystemDomainTaskLibraryResultTaskOccurrenceViewModel[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<TaskListStatus | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [dateFrom, setDateFrom] = React.useState<Date | null>(
    saved?.dateFrom ? new Date(saved.dateFrom) : startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON })
  );
  const [dateTo, setDateTo] = React.useState<Date | null>(
    saved?.dateTo ? new Date(saved.dateTo) : endOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON })
  );
  const [selectedEmployees, setSelectedEmployees] = React.useState<HealthSystemDomainUsersResultUserViewModel[]>([]);

  // Ready immediately when there are no saved IDs to rehydrate; otherwise wait
  // for usersGetUsersAll to resolve so the first fetch includes the employee filter.
  const [employeesReady, setEmployeesReady] = React.useState(!saved?.employeeIds?.length);

  const scheduleMap = useStartOfWorkingDays(taskPlans);

  // ── Rehydrate selected employees once the list is loaded ─────────────────────
  React.useEffect(() => {
    if (!saved?.employeeIds?.length) return;
    apiService.api.users.usersGetUsersAll({}).then((result) => {
      setSelectedEmployees(result.data.filter((e) => saved.employeeIds.includes(e.id)));
      setEmployeesReady(true);
    });
  }, []);

  // ── Persist & fetch ──────────────────────────────────────────────────────────
  const persist = (from: Date | null, to: Date | null, employees: HealthSystemDomainUsersResultUserViewModel[]) => {
    saveFilters<TaskPlansFilters>(storageKey, {
      dateFrom: from?.toISOString() ?? null,
      dateTo: to?.toISOString() ?? null,
      employeeIds: employees.map((e) => e.id)
    });
  };

  const fetch = (from: Date | null, to: Date | null, employees: HealthSystemDomainUsersResultUserViewModel[]) => {
    setIsBusy(true);
    apiService.api.taskList
      .taskListGetDailyTaskPlan({
        DateFrom: from?.toISOString(),
        DateTo: to?.toISOString(),
        EmployeeIds: employees.length > 0 ? employees.map((e) => e.id) : undefined
      })
      .then((result) => setTaskPlans(result.data))
      .catch((e: unknown) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })))
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (employeesReady && !loaded) fetch(dateFrom, dateTo, selectedEmployees);
  }, [employeesReady, loaded]);

  const handleDateFromChange = (value: unknown) => {
    const date = value as Date | null;
    setDateFrom(date);
    persist(date, dateTo, selectedEmployees);
    fetch(date, dateTo, selectedEmployees);
  };

  const handleDateToChange = (value: unknown) => {
    const date = value as Date | null;
    setDateTo(date);
    persist(dateFrom, date, selectedEmployees);
    fetch(dateFrom, date, selectedEmployees);
  };

  const handleClearFilters = () => {
    setStatusFilter(null);
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: WEEK_STARTS_ON });
    const weekEnd = endOfWeek(today, { weekStartsOn: WEEK_STARTS_ON });
    setDateFrom(weekStart);
    setDateTo(weekEnd);
    setSelectedEmployees([]);
    persist(weekStart, weekEnd, []);
    fetch(weekStart, weekEnd, []);
  };

  const handleScheduleWeekSelect = (from: Date, to: Date) => {
    setDateFrom(from);
    setDateTo(to);
    persist(from, to, selectedEmployees);
    fetch(from, to, selectedEmployees);
  };

  const handleEmployeeChange = (employees: HealthSystemDomainUsersResultUserViewModel[]) => {
    setSelectedEmployees(employees);
    persist(dateFrom, dateTo, employees);
    fetch(dateFrom, dateTo, employees);
  };

  const statuses = [
    { value: null, label: t('f-com-all'), dot: 'bg-slate-400' },
    { value: TaskListStatus.ToDo, label: t('f-tasks-lists-items-todo'), dot: 'bg-slate-400' },
    { value: TaskListStatus.InProgress, label: t('f-tasks-lists-items-progress'), dot: 'bg-amber-500' },
    { value: TaskListStatus.Done, label: t('f-task-workspace-completed', 'Completed'), dot: 'bg-emerald-500' }
  ];
  const visiblePlans = taskPlans.filter((plan) => statusFilter === null || plan.status === statusFilter);

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="m-0 text-lg font-semibold text-slate-900">{t('f-tasks-lists')}</h2>
          <p className="mt-1 mb-0 text-sm text-slate-500">
            {t('f-task-workspace-overview-help', 'Follow planned work by date, employee and completion status.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip arrow title={t('m-global-clear-filters')}>
            <IconButton aria-label={t('m-global-clear-filters')} onClick={handleClearFilters} sx={V4_ICON_BUTTON}>
              <FilterListOff />
            </IconButton>
          </Tooltip>
          <ReloadIconButton action={() => setLoaded(false)} />
        </div>
      </div>
      <ScheduleWeekSelector onWeekSelect={handleScheduleWeekSelect} initialDate={dateFrom} />
      <div className="my-5 flex flex-wrap items-start gap-3">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: 'small' } }}
            label={t('f-search-date-from')}
            value={dateFrom}
            onChange={handleDateFromChange}
          />
          <DesktopDatePicker
            format="dd/MM/yyyy"
            slotProps={{ textField: { size: 'small' } }}
            label={t('f-search-date-to')}
            value={dateTo}
            onChange={handleDateToChange}
          />
        </LocalizationProvider>
        <div className="min-w-[220px]">
          <EmployeeSelectModal selectedEmployees={selectedEmployees} onChange={handleEmployeeChange} />
        </div>
      </div>
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label={t('f-com-status')}>
        {statuses.map((status) => (
          <button
            key={status.value ?? 'all'}
            type="button"
            aria-pressed={statusFilter === status.value}
            onClick={() => setStatusFilter(status.value)}
            className={
              'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-indigo-600 ' +
              (statusFilter === status.value
                ? 'border-indigo-200 bg-indigo-50 font-medium text-indigo-700'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            <span className={'h-2 w-2 rounded-full ' + status.dot} />
            {status.label}
            <span className="ml-1 text-xs tabular-nums">
              {status.value === null
                ? taskPlans.length
                : taskPlans.filter((plan) => plan.status === status.value).length}
            </span>
          </button>
        ))}
      </div>
      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
      {loaded && (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs text-slate-500">
              <tr>
                <th scope="col" className="w-12 px-3 py-3">
                  <span className="sr-only">{t('f-com-expand')}</span>
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('f-com-name')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('f-loc-stable')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('f-tasks-lists-assigned-to')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('f-tasks-expected-end-time')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium">
                  {t('f-com-status')}
                </th>
                {showUpdateAction && (
                  <th scope="col" className="px-4 py-3 font-medium">
                    {t('f-com-actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {visiblePlans.map((taskPlan) => (
                <TaskPlanTableRow
                  key={taskPlan.id}
                  taskPlan={taskPlan}
                  showUpdateAction={showUpdateAction}
                  onUpdated={() => setLoaded(false)}
                  startOfWorkingDay={scheduleMap[taskPlan.taskScheduleId]}
                />
              ))}
              {visiblePlans.length === 0 && (
                <tr>
                  <td colSpan={showUpdateAction ? 7 : 6} className="px-4 py-12 text-center">
                    <Icon className="!text-3xl/none text-slate-400">event_available</Icon>
                    <p className="text-slate-500">
                      {t('f-task-workspace-no-results', 'No task lists match these filters.')}
                    </p>
                    <Button onClick={handleClearFilters} color="primary">
                      {t('m-global-clear-filters')}
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TaskPlans;
